/**
 * The navigation tree — a port of `Components/Layout/Sidebar.razor` (585 lines, 30 destinations).
 *
 * Data rather than markup, for the reason every pure module in this port exists: the interesting
 * part is *which* destination sits behind *which* permission, and that is a list to be checked
 * against the server rather than a component to be eyeballed. `Sidebar.tsx` renders it.
 *
 * **The permission keys are the sidebar's, and they are not always the server's.** They are ported
 * verbatim, because matching the original is what makes this a port — but two are worth knowing
 * about, and both are recorded in the plan already:
 *
 * - Everything under `/admin/*` is gated server-side by `RouteAdminRequestAsync`, which checks
 *   `system.admin` before matching any route. So the fine-grained keys here (`system.brand.view`,
 *   `location.view`, `user.view`, …) decide only whether a *link* is shown; an operator holding one
 *   without `system.admin` sees the link and gets a 403. The pages themselves already gate on
 *   `system.admin`, so the refusal is explained rather than mysterious — but the link is the
 *   original's behaviour and changing it is a product decision, not a porting one.
 * - Settings profiles and compliance (`policy.view`, `compliance.view`) are brand-scoped on the
 *   server; a link shows for anyone holding the key on some brand, and the pages show only what
 *   that brand allows.
 */

export interface NavLinkItem {
  label: string
  to: string
  /** A lucide-react icon name; `Sidebar` maps it to a component. */
  icon: string
  /** Match the path exactly rather than as a prefix — for a parent that has children. */
  exact?: boolean
  /** Permission required to see it. Absent means everyone signed in. */
  permission?: string
  /**
   * The permission must be held on a whole brand (or globally), not only at some stores. For a brand's
   * configuration — profiles, groups, rollouts, software, provisioning — which the relay keeps from a
   * user limited to some stores (Plans/delegated-user-management.md).
   */
  brandWide?: boolean
  /** One line saying what the page is for: the command palette shows it beside the page's name. */
  description?: string
  /**
   * The small heading this link sits under inside its section ("Organization", "People"), so a
   * longer section stays scannable. Consecutive links with the same group share one heading.
   */
  group?: string
}

export interface NavSection {
  id: string
  label: string
  icon: string
  permission?: string
  brandWide?: boolean
  /** Open on arrival whatever the page: the section holding the pages used every day. */
  defaultOpen?: boolean
  items: NavLinkItem[]
}

/**
 * A divider with a label, as the Blazor sidebar has above its administration block.
 *
 * Twenty entries in one undifferentiated list is a list nobody reads to the bottom; the heading is
 * what tells an operator where the day-to-day ends and the configuration begins.
 */
export interface NavHeading {
  heading: string
}

export type NavEntry = NavLinkItem | NavSection | NavHeading

export function isHeading(entry: NavEntry): entry is NavHeading {
  return 'heading' in entry
}

export function isSection(entry: NavEntry): entry is NavSection {
  return 'items' in entry
}

/**
 * The tree, built around what an operator does every day (Plans/experience-review-2026-10-10.md,
 * Step 2). About forty equal destinations became a dozen lines:
 *
 * - **The daily pages sit at the top level, one click away:** Dashboard, Devices, Sites, Groups,
 *   Printers, the map and Sessions. They were a "Fleet" section that had to start open to be usable.
 * - **Three sections hold configuration and history:** Changes (every way a change reaches devices:
 *   rollouts, settings profiles, firmware, Windows updates, agent versions, rollout plans), Jobs, and
 *   Insights (compliance, activity, system logs).
 * - **Enrollment** (adding, approving and registering devices) is its own section, since it is
 *   regular operational work.
 * - **Administration** is an expandable section, under small headings: Organization, People, and
 *   Server and security. It was a hub page (`/admin`) for a while; Paul preferred the links in the
 *   sidebar, one click away, with no page in between.
 *
 * **A link is shown only where its page will open.** Managing sites and Server Settings are served by the
 * relay's admin router, which requires `system.admin`, so their links carry that permission rather
 * than a finer key that led to a refusal.
 */
const ADMIN_GROUPS: NavSection[] = [
  {
    id: 'admin-organization',
    label: 'Organization',
    icon: 'Building2',
    items: [
      {
        label: 'Brands',
        to: '/admin/brands',
        icon: 'Building2',
        permission: 'system.brand.view',
        description: 'The businesses whose devices this server manages.',
      },
      {
        label: 'Organizations',
        to: '/admin/organizations',
        icon: 'Building2',
        permission: 'org.view',
        description: "Companies that work on brands' devices, and what each may reach.",
      },
      {
        label: 'Manage Sites',
        to: '/admin/locations',
        icon: 'MapPin',
        permission: 'system.admin',
        description: 'Add, rename and remove sites, and set their numbers and addresses.',
      },
      {
        label: 'Site Groups',
        to: '/admin/location-groups',
        icon: 'Layers',
        permission: 'location.group.manage',
        description: 'Sets of sites a person can be limited to.',
      },
      {
        label: 'API Keys',
        to: '/admin/api-keys',
        icon: 'KeyRound',
        permission: 'api_keys.manage',
        description: "Keys your organization's integrations use with the public API.",
      },
    ],
  },
  {
    id: 'admin-people',
    label: 'People',
    icon: 'Users',
    items: [
      {
        label: 'Users',
        to: '/admin/users',
        icon: 'Users',
        permission: 'user.view',
        description: 'Who can sign in, and what each person can reach.',
      },
      {
        label: 'Roles',
        to: '/admin/roles',
        icon: 'ShieldCheck',
        permission: 'system.admin',
        description: 'What each role is allowed to do.',
      },
    ],
  },
  {
    id: 'admin-enrollment',
    label: 'Onboarding',
    icon: 'PackagePlus',
    items: [
      {
        label: 'Add Devices',
        to: '/provisioning/add',
        icon: 'PackagePlus',
        permission: 'provisioning.manage',
        brandWide: true,
        description: 'Install links and setup for new Windows, Linux and Android devices.',
      },
      {
        label: 'Expected Devices',
        to: '/provisioning/expected',
        icon: 'ListChecks',
        permission: 'provisioning.view',
        brandWide: true,
        description: 'Serial numbers you expect, so they enroll without approval.',
      },
      {
        label: 'Enrollment Queue',
        to: '/provisioning/queue',
        icon: 'Inbox',
        permission: 'provisioning.view',
        brandWide: true,
        description: 'Devices that asked to enroll and need a decision.',
      },
      // Everything below is for looking into enrollment after the fact, or for setting it up once.
      // Adding a device needs none of it, and six more links at equal weight was the reason
      // "where is the thing I need" was hard to answer (Plans/esper-ux-parity.md, Phase 5).
      {
        label: 'Claims',
        to: '/provisioning/claims',
        icon: 'ShieldCheck',
        permission: 'provisioning.view',
        brandWide: true,
        group: 'More',
        description: 'Devices waiting to be accepted, and the files that enroll them.',
      },
      {
        label: 'Enrollment Activity',
        to: '/provisioning/activity',
        icon: 'Activity',
        permission: 'provisioning.view',
        brandWide: true,
        group: 'More',
        description: 'Every enrollment attempt, accepted or not.',
      },
      {
        label: 'Pairing Codes',
        to: '/provisioning/pairing',
        icon: 'Link2',
        permission: 'provisioning.view',
        brandWide: true,
        group: 'More',
        description: 'One-time codes that pair a device by hand.',
      },
      {
        label: 'Type Rules',
        to: '/provisioning/type-rules',
        icon: 'Layers',
        permission: 'provisioning.view',
        brandWide: true,
        group: 'More',
        description: "How a new device's type is worked out from what it reports.",
      },
      {
        label: 'Provisioning API Keys',
        to: '/provisioning/api-keys',
        icon: 'KeyRound',
        permission: 'system.admin',
        group: 'More',
        description: 'Keys for factory and release scripts.',
      },
    ],
  },
  {
    id: 'admin-registration',
    label: 'Registration',
    icon: 'Server',
    items: [
      {
        label: 'Registered Devices',
        to: '/admin/devices',
        icon: 'Server',
        permission: 'device.manage',
        description: 'Register, rename, move or retire devices.',
      },
      {
        label: 'Device Types',
        to: '/admin/device-types',
        icon: 'Layers',
        permission: 'device.type.manage',
        description: 'Registers, kitchen displays, routers and the other kinds of device.',
      },
    ],
  },
  {
    id: 'admin-server',
    label: 'Server and Security',
    icon: 'Settings',
    items: [
      {
        label: 'Server Settings',
        to: '/admin/settings',
        icon: 'Settings',
        permission: 'system.admin',
        description: 'Server-wide options, such as recording remote sessions.',
      },
      {
        label: 'Alert Rules',
        to: '/alerts/setup',
        icon: 'Bell',
        permission: 'alerts.manage',
        description: 'What counts as an alert, escalation, and PagerDuty, Slack, Teams and webhooks.',
      },
      {
        label: 'Authorization Dashboard',
        to: '/admin/authorization',
        icon: 'ShieldCheck',
        permission: 'system.audit.view',
        description: 'Who can do what, at a glance.',
      },
      {
        label: 'Authorization Audit Log',
        to: '/admin/audit/authorization',
        icon: 'FileSearch',
        permission: 'system.audit.view',
        description: 'Every permission check the server made, allowed or refused.',
      },
    ],
  },
]

/** A section's links from groups of them, each link carrying its group's heading. */
function grouped(...ids: string[]): NavLinkItem[] {
  return ids.flatMap((id) => {
    const group = ADMIN_GROUPS.find((g) => g.id === id)!
    return group.items.map((item) => ({ ...item, group: group.label }))
  })
}

export const NAVIGATION: NavEntry[] = [
  { label: 'Dashboard', to: '/', icon: 'LayoutGrid', exact: true },
  { label: 'Devices', to: '/devices', icon: 'MonitorSmartphone' },
  { label: 'Sites', to: '/sites', icon: 'MapPin' },
  { label: 'Groups', to: '/groups', icon: 'Boxes', permission: 'group.view', brandWide: true },
  { label: 'Printers', to: '/printers', icon: 'Printer', permission: 'printer.view' },
  { label: 'Map', to: '/map', icon: 'Map' },
  { label: 'Sessions', to: '/sessions', icon: 'Zap' },
  {
    id: 'changes',
    label: 'Changes',
    // Four daily destinations at the top; Agent Versions (a read-only summary of what the
    // fleet runs) and Rollout Plans (staging patterns, set once) sit under a More heading, as
    // Enrollment's set-up-once pages do. Seven links at equal weight is the density the
    // reviewer called overwhelming (Plans/esper-ux-parity.md, Phase 7).
    icon: 'Rocket',
    items: [
      {
        label: 'Rollouts',
        to: '/rollouts',
        icon: 'Rocket',
        permission: 'deployment.view',
        brandWide: true,
      },
      {
        label: 'Settings Profiles',
        to: '/settings/profiles',
        icon: 'FileStack',
        permission: 'policy.view',
        brandWide: true,
      },
      {
        label: 'Setting Explorer',
        to: '/settings/explorer',
        icon: 'Search',
        permission: 'policy.view',
        brandWide: true,
      },
      {
        label: 'Firmware',
        to: '/settings/firmware',
        icon: 'HardDrive',
        permission: 'software.view',
        brandWide: true,
      },
      {
        label: 'Windows Updates',
        to: '/settings/windows-updates',
        icon: 'CloudDownload',
        permission: 'software.view',
        brandWide: true,
      },
      {
        label: 'Agent Versions',
        group: 'More',
        to: '/settings/agent-versions',
        icon: 'Layers',
        permission: 'device.view',
      },
      {
        label: 'Rollout Plans',
        group: 'More',
        to: '/rollout-plans',
        icon: 'ListOrdered',
        permission: 'deployment.view',
        brandWide: true,
      },
    ],
  },
  {
    id: 'jobs',
    label: 'Jobs',
    icon: 'Briefcase',
    items: [
      { label: 'Job Library', to: '/jobs', icon: 'List', exact: true },
      { label: 'Schedules', to: '/jobs/schedules', icon: 'Clock' },
      { label: 'Run History', to: '/jobs/runs', icon: 'FileText' },
      { label: 'Job Queue', to: '/jobs/queue', icon: 'ListOrdered' },
      { label: 'Approvals', to: '/jobs/approvals', icon: 'CheckCircle' },
      { label: 'Variables', to: '/jobs/variables', icon: 'Variable' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: 'ShieldCheck',
    items: [
      {
        label: 'Alerts',
        to: '/alerts',
        icon: 'Bell',
        permission: 'device.view',
        description: 'Devices, sites and printers that need attention now.',
      },
      {
        label: 'Compliance',
        to: '/insights/compliance',
        icon: 'ShieldCheck',
        permission: 'compliance.view',
      },
      { label: 'Activity', to: '/activity', icon: 'Activity', permission: 'activity.view' },
      {
        label: 'System Logs',
        to: '/system-logs',
        icon: 'ScrollText',
        permission: 'device.logs.view',
      },
    ],
  },
  // Getting devices into the fleet is regular operational work, so it has its own section; what is
  // left under Administration is set up once and rarely changed (Plans/experience-review-2026-10-10.md).
  {
    id: 'enrollment',
    label: 'Enrollment',
    icon: 'PackagePlus',
    items: grouped('admin-enrollment', 'admin-registration'),
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: 'Settings',
    items: grouped('admin-organization', 'admin-people', 'admin-server'),
  },
]

/**
 * The tree as this operator may see it.
 *
 * A section whose every child is refused is dropped rather than rendered empty: a heading that
 * expands to nothing reads as a page that failed to load, which is the same complaint this port
 * fixed on the maintenance-window location list.
 */
export function visibleNavigation(
  hasPermission: (key: string) => boolean,
  tree: NavEntry[] = NAVIGATION,
  hasBrandWidePermission: (key: string) => boolean = hasPermission,
): NavEntry[] {
  const allowed = ({ permission, brandWide }: { permission?: string; brandWide?: boolean }) =>
    permission === undefined || (brandWide ? hasBrandWidePermission : hasPermission)(permission)

  const kept = tree.flatMap((entry): NavEntry[] => {
    if (isHeading(entry)) return [entry]
    if (!isSection(entry)) return allowed(entry) ? [entry] : []
    if (!allowed(entry)) return []
    const items = entry.items.filter((item) => allowed(item))
    return items.length > 0 ? [{ ...entry, items }] : []
  })

  // A heading with nothing under it is a label for an empty region. Drop it rather than leave an
  // "Administration" rule above the end of the list for an operator who administers nothing.
  return kept.filter(
    (entry, i) => !isHeading(entry) || kept.slice(i + 1).some((e) => !isHeading(e)),
  )
}

/** True when `path` is at or below `to` — the rule NavLink's non-exact matching uses. */
export function isPathActive(path: string, to: string, exact = false): boolean {
  if (exact) return path === to
  if (to === '/') return path === '/'
  return path === to || path.startsWith(`${to}/`)
}

/** The sections that start open whatever the page (see `NavSection.defaultOpen`). */
export function defaultOpenSections(tree: NavEntry[] = NAVIGATION): string[] {
  return tree
    .filter(isSection)
    .filter((s) => s.defaultOpen)
    .map((s) => s.id)
}

/**
 * The section that should be open on arrival, so a deep link does not land with its own branch
 * collapsed and nothing in the sidebar indicating where it is.
 */
export function sectionForPath(path: string, tree: NavEntry[] = NAVIGATION): string | null {
  for (const entry of tree) {
    if (isHeading(entry) || !isSection(entry)) continue
    if (entry.items.some((item) => isPathActive(path, item.to, item.exact))) return entry.id
  }
  return null
}
